import { Causes } from '@platform/client';
import { OrganizationApplicationParameters } from '@platform/client/schemas';
import { FormsCheckBox, MultiSelectDropdown } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/SDGvisual.svg';

type FormValues = {
  sdgs?: string[];
  noSDGs?: boolean;
};

export const OrgSDGSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const sdgs = Causes.useGetCauses();

  const sdgOptions =
    sdgs.isSuccess ?
      sdgs.data.map((sdg) => ({
        value: sdg.name,
        label: sdg.shortName,
        description: sdg.shortDescription,
      }))
    : [];

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      sdgs: getApplicationDataByKey('sdgs') || [],
      noSDGs: getApplicationDataByKey('sdgs') && getApplicationDataByKey('sdgs').length === 0,
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      sdgs: data.sdgs,
    });
    onContinue();
  };

  const onSelectionChange = (value: string[]) => {
    setValue('sdgs', value);
  };

  const handleBack = () => {
    updateApplicationData({
      sdgs: watch('sdgs'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title='Which SDGs does your organization work toward?'
        description={
          'These are based on the 17 UN Sustainable Development Goals. Select up to two that best reflect your main impact areas.'
        }
      >
        <MultiSelectDropdown
          register={() => {
            return register('sdgs', {
              validate: () => {
                if (!watch('noSDGs') && watch('sdgs')?.length === 0) {
                  return 'Select up to two SDG, or confirm your organization does not contribute to SDGs to continue.';
                }
              },
            });
          }}
          selectedItems={watch('sdgs') || []}
          onSelectionChange={onSelectionChange}
          options={sdgOptions}
          label='Select up to two primary SDGs'
          selectionLimit={2}
          placeholder='Select up to two SDGs'
          error={errors.sdgs?.message}
        />
        <FormsCheckBox
          control={control}
          description='Our organization does not contribute to SDGs'
          size='md'
          name='noSDGs'
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
